// src/components/auth/authUtils.js
/**
 * Authentication & Authorization Utilities
 * Core functions for permission checking, role validation, and auth state management
 * 
 * ✅ UPDATED VERSION - Added saveAuthData() function
 */

import { USER_ROLES, PERMISSIONS, USER_STATUS, isAdminRole, isDirectorRole, isManagerRole, isFieldRole } from './constants';

// ============================================
// STORAGE HELPERS
// ============================================

/**
 * Get storage (sessionStorage or localStorage based on rememberMe)
 */
const getStorage = () => {
  const rememberMe = sessionStorage.getItem('rememberMe') === 'true';
  return rememberMe ? localStorage : sessionStorage;
};

/**
 * Get item from appropriate storage
 */
const getStorageItem = (key) => {
  // Check sessionStorage first (active session)
  let item = sessionStorage.getItem(key);
  if (item) return item;
  
  // Check localStorage (remembered session)
  item = localStorage.getItem(key);
  return item;
};

/**
 * Set item to appropriate storage
 */
const setStorageItem = (key, value) => {
  const storage = getStorage();
  storage.setItem(key, value);
};

/**
 * Remove item from both storages
 */
const removeStorageItem = (key) => {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
};

// ============================================
// AUTHENTICATION STATE
// ============================================

/**
 * ✅ NEW FUNCTION - Save authentication data from login response
 * 
 * This is the CRITICAL function that was missing!
 * It properly saves the login response including permissions.
 * 
 * @param {Object} loginResponse - Response from /api/auth/login
 * @param {boolean} rememberMe - Save to localStorage if true (default: false = sessionStorage)
 * 
 * Expected loginResponse format from backend:
 * {
 *   accessToken: "eyJhbGc...",
 *   refreshToken: "eyJhbGc...",
 *   user: {
 *     id: 1,
 *     username: "admin",
 *     email: "admin@erabangun.co.id",
 *     fullName: "Administrator",
 *     role: "ADMINISTRATOR",
 *     status: "ACTIVE",
 *     canAccessWeb: true,
 *     canAccessMobile: false
 *   },
 *   permissions: ["USER_VIEW", "USER_CREATE", "USER_UPDATE", ...]
 * }
 * 
 * @returns {boolean} True if save successful
 */
export const saveAuthData = (loginResponse, rememberMe = false) => {
  const storage = rememberMe ? localStorage : sessionStorage;
  
  try {
    // Validate input
    if (!loginResponse || !loginResponse.accessToken) {
      console.error('❌ Invalid login response - missing accessToken');
      return false;
    }
    
    // Save tokens
    storage.setItem('token', loginResponse.accessToken);
    
    if (loginResponse.refreshToken) {
      storage.setItem('refreshToken', loginResponse.refreshToken);
    }
    
    // ✅ CRITICAL FIX: Merge permissions into user object
    // Backend sends permissions separately, but our authUtils expects it inside user object
    const userWithPermissions = {
      ...loginResponse.user,
      permissions: loginResponse.permissions || []
    };
    
    // Save merged user object
    storage.setItem('user', JSON.stringify(userWithPermissions));
    
    // Save convenience fields for quick access
    if (loginResponse.user) {
      const displayName = loginResponse.user.fullName || loginResponse.user.username || '';
      storage.setItem('username', displayName);
      storage.setItem('userEmail', loginResponse.user.email || '');
    }
    
    // Set authentication flag
    storage.setItem('isAuthenticated', 'true');
    
    // Set remember me flag if applicable
    if (rememberMe) {
      storage.setItem('rememberMe', 'true');
    }
    
    // Debug log in development mode
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Auth data saved successfully:', {
        storage: rememberMe ? 'localStorage' : 'sessionStorage',
        username: loginResponse.user?.username,
        role: loginResponse.user?.role,
        permissionsCount: loginResponse.permissions?.length || 0,
        permissions: loginResponse.permissions,
        user: userWithPermissions
      });
    }
    
    return true;
  } catch (error) {
    console.error('❌ Error saving auth data:', error);
    return false;
  }
};

/**
 * Check if user is authenticated
 * @returns {boolean}
 */
export const isAuthenticated = () => {
  const token = getStorageItem('token');
  const isAuth = getStorageItem('isAuthenticated') === 'true';
  return !!(token && isAuth);
};

/**
 * Get current access token
 * @returns {string|null}
 */
export const getToken = () => {
  return getStorageItem('token');
};

/**
 * Get refresh token
 * @returns {string|null}
 */
export const getRefreshToken = () => {
  return getStorageItem('refreshToken');
};

/**
 * Set authentication tokens
 * @param {string} accessToken
 * @param {string} refreshToken
 */
export const setTokens = (accessToken, refreshToken) => {
  setStorageItem('token', accessToken);
  setStorageItem('refreshToken', refreshToken);
  setStorageItem('isAuthenticated', 'true');
};

/**
 * Clear all authentication data
 */
export const clearAuth = () => {
  removeStorageItem('token');
  removeStorageItem('refreshToken');
  removeStorageItem('user');
  removeStorageItem('isAuthenticated');
  removeStorageItem('username');
  removeStorageItem('userEmail');
  removeStorageItem('rememberMe');
};

/**
 * Logout user
 */
export const logout = () => {
  clearAuth();
  window.location.href = '/login';
};

// ============================================
// USER INFORMATION
// ============================================

/**
 * Get current user object
 * @returns {Object|null}
 */
export const getCurrentUser = () => {
  try {
    const userStr = getStorageItem('user');
    if (!userStr) return null;
    return JSON.parse(userStr);
  } catch (error) {
    console.error('Error parsing user data:', error);
    return null;
  }
};

/**
 * Get current user ID
 * @returns {number|null}
 */
export const getUserId = () => {
  const user = getCurrentUser();
  return user?.id || null;
};

/**
 * Get current username
 * @returns {string|null}
 */
export const getUsername = () => {
  const user = getCurrentUser();
  return user?.username || getStorageItem('username');
};

/**
 * Get current user's full name
 * @returns {string|null}
 */
export const getUserFullName = () => {
  const user = getCurrentUser();
  return user?.fullName || user?.name || null;
};

/**
 * Get current user's email
 * @returns {string|null}
 */
export const getUserEmail = () => {
  const user = getCurrentUser();
  return user?.email || getStorageItem('userEmail');
};

/**
 * Get current user's role
 * @returns {string|null}
 */
export const getUserRole = () => {
  const user = getCurrentUser();
  return user?.role || null;
};

/**
 * Get current user's status
 * @returns {string|null}
 */
export const getUserStatus = () => {
  const user = getCurrentUser();
  return user?.status || null;
};

/**
 * Check if user account is active
 * @returns {boolean}
 */
export const isUserActive = () => {
  const status = getUserStatus();
  return status === USER_STATUS.ACTIVE;
};

// ============================================
// PERMISSIONS
// ============================================

/**
 * Get user's permissions array
 * 
 * ✅ This function now works correctly because saveAuthData()
 *    properly merges permissions into the user object
 * 
 * @returns {string[]}
 */
export const getPermissions = () => {
  try {
    const user = getCurrentUser();
    const permissions = user?.permissions || [];
    
    // Debug log in development if permissions are empty
    if (process.env.NODE_ENV === 'development' && permissions.length === 0) {
      console.warn('⚠️ No permissions found for user:', user);
    }
    
    return permissions;
  } catch (error) {
    console.error('Error getting permissions:', error);
    return [];
  }
};

/**
 * Check if user has specific permission
 * @param {string} permission - Permission code (e.g., 'USER_CREATE')
 * @returns {boolean}
 */
export const hasPermission = (permission) => {
  if (!isAuthenticated()) return false;
  if (!isUserActive()) return false;
  
  const permissions = getPermissions();
  const hasAccess = permissions.includes(permission);
  
  // Debug log in development when permission is denied
  if (process.env.NODE_ENV === 'development' && !hasAccess) {
    console.log(`🔒 Permission denied: ${permission}`, {
      userPermissions: permissions,
      required: permission
    });
  }
  
  return hasAccess;
};

/**
 * Check if user has ANY of the specified permissions
 * @param {string[]} permissionList - Array of permission codes
 * @returns {boolean}
 */
export const hasAnyPermission = (permissionList) => {
  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return false;
  }
  
  if (!isAuthenticated()) return false;
  if (!isUserActive()) return false;
  
  const userPermissions = getPermissions();
  return permissionList.some(permission => userPermissions.includes(permission));
};

/**
 * Check if user has ALL of the specified permissions
 * @param {string[]} permissionList - Array of permission codes
 * @returns {boolean}
 */
export const hasAllPermissions = (permissionList) => {
  if (!Array.isArray(permissionList) || permissionList.length === 0) {
    return false;
  }
  
  if (!isAuthenticated()) return false;
  if (!isUserActive()) return false;
  
  const userPermissions = getPermissions();
  return permissionList.every(permission => userPermissions.includes(permission));
};

/**
 * Check if user has permission for resource and action
 * @param {string} resource - Resource name (e.g., 'USER', 'PROJECT')
 * @param {string} action - Action name (e.g., 'CREATE', 'VIEW')
 * @returns {boolean}
 */
export const hasResourcePermission = (resource, action) => {
  const permission = `${resource.toUpperCase()}_${action.toUpperCase()}`;
  return hasPermission(permission);
};

// ============================================
// ROLE CHECKS
// ============================================

/**
 * Check if user has specific role
 * @param {string} role - Role code (e.g., 'ADMINISTRATOR')
 * @returns {boolean}
 */
export const hasRole = (role) => {
  if (!isAuthenticated()) return false;
  
  const userRole = getUserRole();
  return userRole === role;
};

/**
 * Check if user has ANY of the specified roles
 * @param {string[]} roleList - Array of role codes
 * @returns {boolean}
 */
export const hasAnyRole = (roleList) => {
  if (!Array.isArray(roleList) || roleList.length === 0) {
    return false;
  }
  
  if (!isAuthenticated()) return false;
  
  const userRole = getUserRole();
  return roleList.includes(userRole);
};

/**
 * Check if user is Administrator
 * @returns {boolean}
 */
export const isAdmin = () => {
  const role = getUserRole();
  return role === USER_ROLES.ADMINISTRATOR;
};

/**
 * Check if user is Director (any director role)
 * @returns {boolean}
 */
export const isDirector = () => {
  const role = getUserRole();
  return isDirectorRole(role);
};

/**
 * Check if user is Manager (any manager role)
 * @returns {boolean}
 */
export const isManager = () => {
  const role = getUserRole();
  return isManagerRole(role);
};

/**
 * Check if user is Field Worker (Supervisor or Mandor)
 * @returns {boolean}
 */
export const isFieldWorker = () => {
  const role = getUserRole();
  return isFieldRole(role);
};

/**
 * Check if user is Supervisor
 * @returns {boolean}
 */
export const isSupervisor = () => {
  return hasRole(USER_ROLES.SUPERVISOR);
};

/**
 * Check if user is Mandor
 * @returns {boolean}
 */
export const isMandor = () => {
  return hasRole(USER_ROLES.MANDOR);
};

// ============================================
// ACCESS CONTROL
// ============================================

/**
 * Check if user can access web application
 * @returns {boolean}
 */
export const canAccessWeb = () => {
  const user = getCurrentUser();
  // If not explicitly set, default to true
  if (user?.canAccessWeb === undefined) return true;
  return user.canAccessWeb === true;
};

/**
 * Check if user can access mobile application
 * @returns {boolean}
 */
export const canAccessMobile = () => {
  const user = getCurrentUser();
  // If explicitly set, use that value
  if (user?.canAccessMobile !== undefined) {
    return user.canAccessMobile === true;
  }
  // Otherwise, check if field worker (default mobile access)
  return isFieldWorker();
};

// ============================================
// PERMISSION CATEGORIES
// ============================================

/**
 * Get all permissions for a category
 * @param {string} category - Category name (e.g., 'User Management')
 * @returns {string[]}
 */
export const getPermissionsByCategory = (category) => {
  const { PERMISSION_CATEGORIES } = require('./constants');
  return PERMISSION_CATEGORIES[category] || [];
};

/**
 * Check if user has all permissions in a category
 * @param {string} category - Category name
 * @returns {boolean}
 */
export const hasCategoryPermissions = (category) => {
  const categoryPermissions = getPermissionsByCategory(category);
  return hasAllPermissions(categoryPermissions);
};

/**
 * Check if user has any permission in a category
 * @param {string} category - Category name
 * @returns {boolean}
 */
export const hasAnyCategoryPermission = (category) => {
  const categoryPermissions = getPermissionsByCategory(category);
  return hasAnyPermission(categoryPermissions);
};

// ============================================
// CONVENIENCE SHORTCUTS
// ============================================

/**
 * User Management Permissions
 */
export const canCreateUser = () => hasPermission(PERMISSIONS.USER_CREATE);
export const canViewUser = () => hasPermission(PERMISSIONS.USER_VIEW);
export const canUpdateUser = () => hasPermission(PERMISSIONS.USER_UPDATE);
export const canDeleteUser = () => hasPermission(PERMISSIONS.USER_DELETE);

/**
 * Project Management Permissions
 */
export const canCreateProject = () => hasPermission(PERMISSIONS.PROJECT_CREATE);
export const canViewProject = () => hasPermission(PERMISSIONS.PROJECT_VIEW);
export const canUpdateProject = () => hasPermission(PERMISSIONS.PROJECT_UPDATE);
export const canDeleteProject = () => hasPermission(PERMISSIONS.PROJECT_DELETE);

/**
 * Budget Management Permissions
 */
export const canCreateBudget = () => hasPermission(PERMISSIONS.BUDGET_CREATE);
export const canViewBudget = () => hasPermission(PERMISSIONS.BUDGET_VIEW);
export const canUpdateBudget = () => hasPermission(PERMISSIONS.BUDGET_UPDATE);
export const canDeleteBudget = () => hasPermission(PERMISSIONS.BUDGET_DELETE);
export const canApproveBudget = () => hasPermission(PERMISSIONS.BUDGET_APPROVE);

/**
 * Milestone Management Permissions
 */
export const canCreateMilestone = () => hasPermission(PERMISSIONS.MILESTONE_CREATE);
export const canViewMilestone = () => hasPermission(PERMISSIONS.MILESTONE_VIEW);
export const canUpdateMilestone = () => hasPermission(PERMISSIONS.MILESTONE_UPDATE);
export const canDeleteMilestone = () => hasPermission(PERMISSIONS.MILESTONE_DELETE);
export const canApproveMilestone = () => hasPermission(PERMISSIONS.MILESTONE_APPROVE);

/**
 * Evidence Management Permissions
 */
export const canUploadEvidence = () => hasPermission(PERMISSIONS.EVIDENCE_UPLOAD);
export const canViewEvidence = () => hasPermission(PERMISSIONS.EVIDENCE_VIEW);
export const canDeleteEvidence = () => hasPermission(PERMISSIONS.EVIDENCE_DELETE);
export const canApproveEvidence = () => hasPermission(PERMISSIONS.EVIDENCE_APPROVE);

/**
 * Report Permissions
 */
export const canViewReports = () => hasPermission(PERMISSIONS.REPORT_VIEW);
export const canExportReports = () => hasPermission(PERMISSIONS.REPORT_EXPORT);

/**
 * System Permissions
 */
export const canManageSystem = () => hasPermission(PERMISSIONS.SYSTEM_SETTINGS);
export const canViewAudit = () => hasPermission(PERMISSIONS.AUDIT_VIEW);

// ============================================
// DEBUGGING HELPERS
// ============================================

/**
 * Log current auth state (for debugging)
 */
export const debugAuthState = () => {
  if (process.env.NODE_ENV !== 'development') return;
  
  console.group('🔐 Auth State Debug');
  console.log('Authenticated:', isAuthenticated());
  console.log('User:', getCurrentUser());
  console.log('Role:', getUserRole());
  console.log('Permissions:', getPermissions());
  console.log('Is Admin:', isAdmin());
  console.log('Is Director:', isDirector());
  console.log('Is Manager:', isManager());
  console.log('Is Field Worker:', isFieldWorker());
  console.log('Can Access Web:', canAccessWeb());
  console.log('Can Access Mobile:', canAccessMobile());
  console.groupEnd();
};

/**
 * Check and log specific permission (for debugging)
 * @param {string} permission
 */
export const debugPermission = (permission) => {
  if (process.env.NODE_ENV !== 'development') return;
  
  console.group(`🔑 Permission Check: ${permission}`);
  console.log('Has Permission:', hasPermission(permission));
  console.log('User Permissions:', getPermissions());
  console.log('User Role:', getUserRole());
  console.groupEnd();
};

// ============================================
// EXPORT ALL
// ============================================

export default {
  // Auth state management
  saveAuthData,  // ✅ NEW EXPORT
  isAuthenticated,
  getToken,
  getRefreshToken,
  setTokens,
  clearAuth,
  logout,
  
  // User info
  getCurrentUser,
  getUserId,
  getUsername,
  getUserFullName,
  getUserEmail,
  getUserRole,
  getUserStatus,
  isUserActive,
  
  // Permissions
  getPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  hasResourcePermission,
  
  // Roles
  hasRole,
  hasAnyRole,
  isAdmin,
  isDirector,
  isManager,
  isFieldWorker,
  isSupervisor,
  isMandor,
  
  // Access control
  canAccessWeb,
  canAccessMobile,
  
  // Categories
  getPermissionsByCategory,
  hasCategoryPermissions,
  hasAnyCategoryPermission,
  
  // Shortcuts
  canCreateUser,
  canViewUser,
  canUpdateUser,
  canDeleteUser,
  canCreateProject,
  canViewProject,
  canUpdateProject,
  canDeleteProject,
  canCreateBudget,
  canViewBudget,
  canUpdateBudget,
  canDeleteBudget,
  canApproveBudget,
  canCreateMilestone,
  canViewMilestone,
  canUpdateMilestone,
  canDeleteMilestone,
  canApproveMilestone,
  canUploadEvidence,
  canViewEvidence,
  canDeleteEvidence,
  canApproveEvidence,
  canViewReports,
  canExportReports,
  canManageSystem,
  canViewAudit,
  
  // Debug
  debugAuthState,
  debugPermission,
};